/**
 * "Common errors" — what students at ONE school most often get wrong in ONE
 * Practice topic, summarised by the model once a day from the wrong options
 * they chose in Practice → Practice by topic.
 *
 * The stored summary and the API response share this schema: the worker
 * validates what it writes, and the API re-parses what it reads, so a row
 * written by anything else (a hand-run script, an older deploy) is dropped
 * rather than half-rendered on a student's screen.
 *
 * Counts are computed by the server from the attempts each error was matched
 * to, never taken from the model. Nothing here identifies an option, and no
 * field carries an answer key — the summary is shown BEFORE a practice
 * session on the very questions it was drawn from.
 */
import { z } from 'zod';

export const topicCommonErrorSchema = z.object({
  /** The mistake in a few words, sentence case. */
  title: z.string().trim().min(1).max(120),
  /** One sentence: the faulty reasoning behind it. */
  misconception: z.string().trim().min(1).max(400),
  /** One sentence: the check or idea that prevents it. */
  howToAvoid: z.string().trim().min(1).max(400),
  /** Wrong attempts at this school that this error accounts for. */
  attempts: z.number().int().nonnegative(),
  /** Distinct students behind those attempts. */
  students: z.number().int().nonnegative(),
  /** The questions it was seen on. */
  questionIds: z.array(z.string().min(1)).max(20),
});
export type TopicCommonError = z.infer<typeof topicCommonErrorSchema>;

/** At most five: a briefing is read in the seconds before "Start practice". */
export const TOPIC_COMMON_ERRORS_MAX = 5;

export const topicCommonErrorsSchema = z.array(topicCommonErrorSchema).max(TOPIC_COMMON_ERRORS_MAX);

/** GET /practice/topics/:topicId/common-errors */
export interface TopicCommonErrorsDTO {
  topicId: string;
  topicName: string;
  /** Empty when there is no summary yet — too little practice data, or none. */
  errors: TopicCommonError[];
  /** Wrong attempts the summary was built from. 0 when there is none. */
  sampleSize: number;
  /** Distinct students the summary was built from. */
  studentCount: number;
  /** ISO time the summary was written; null when there is none. */
  refreshedAt: string | null;
}
